import { EditableEvent, EventType, Session, Reminder, EmailReminder } from 'types/event.types';
import dayjs from 'utils/extendedDayJS';
import { REMINDERS_GUEST_LIST, REMINDER_STATUS } from 'constants/enums';

interface DateTimeValues {
  datetimeScheduled?: string;
  datetimeEnd?: string;
  timezoneCode?: string;
  allDay?: boolean;
}

// TODO: add unit tests
export const getUpdatedDateTimeValues = (
  newValues: DateTimeValues,
  oldValues: DateTimeValues
): DateTimeValues => {
  const updatedValues = { ...oldValues, ...newValues };
  if (newValues.timezoneCode && updatedValues.allDay) {
    updatedValues.datetimeScheduled = dayjs.updateDateTime({
      dateTime: updatedValues.datetimeScheduled,
      timezoneCode: oldValues.timezoneCode,
      newTimezoneCode: updatedValues.timezoneCode
    });
    updatedValues.datetimeEnd = dayjs.updateDateTime({
      dateTime: updatedValues.datetimeEnd,
      timezoneCode: oldValues.timezoneCode,
      newTimezoneCode: updatedValues.timezoneCode
    });
  }
  // Ensure end datetime is set to a reasonable datetime after the start datetime
  const diffBetweenStartAndEnd = dayjs(updatedValues.datetimeScheduled).diff(
    dayjs(updatedValues.datetimeEnd)
  );
  if (diffBetweenStartAndEnd >= 0) {
    updatedValues.datetimeEnd = dayjs.updateDateTime({
      dateTime: updatedValues.datetimeScheduled,
      timezoneCode: updatedValues.timezoneCode,
      newDateTime: updatedValues.datetimeEnd
    });
  }

  return updatedValues;
};

/**
 * Calculate the earliest start date and latest end date from all sessions
 * and return them as an object with datetimeScheduled and datetimeEnd properties
 *
 * If no sessions are provided, return null
 *
 * Exported for testing only
 *
 * @param sessions Event sessions array
 * @param timezoneCode Event timezone code
 * @returns
 */
export const getDateTimeValuesFromSessions = (
  sessions?: Session[],
  timezoneCode?: string
): DateTimeValues | null => {
  if (!sessions || !sessions.length) {
    return null;
  }

  // Get the earliest start date and latest end date from all sessions
  const { earliestStartDate, latestEndDate } = sessions.reduce(
    (acc: any, session: Session) => {
      const sessionStart = session.allDay
        ? dayjs.convertToTimezone(session.datetimeScheduled, timezoneCode).startOf('day')
        : dayjs.convertToTimezone(session.datetimeScheduled, timezoneCode);
      const sessionEnd = session.allDay
        ? dayjs.convertToTimezone(session.datetimeEnd, timezoneCode).endOf('day')
        : dayjs.convertToTimezone(session.datetimeEnd, timezoneCode);

      if (!acc.earliestStartDate || sessionStart.isBefore(acc.earliestStartDate)) {
        acc.earliestStartDate = sessionStart;
      }

      if (!acc.latestEndDate || sessionEnd.isAfter(acc.latestEndDate)) {
        acc.latestEndDate = sessionEnd;
      }

      return acc;
    },
    { earliestStartDate: null, latestEndDate: null }
  );

  return {
    datetimeScheduled: earliestStartDate,
    datetimeEnd: latestEndDate
  };
};

/**
 * Get calculated datetimeScheduled and datetimeEnd values for Multi-sessions event,
 * otherwise return the same values from the formValues
 *
 * This should be used in any place where it depends on the event's datetimeScheduled and datetimeEnd values,
 * i.e. In the EditEventCommunications to display relative time to the event's datetimeScheduled in the reminder header.
 *
 * @param formValues Edit event form values
 * @returns
 */
export const getEventFormDateTimeValues = (formValues: EditableEvent): DateTimeValues => {
  if (formValues.eventType === EventType.MULTI_SESSIONS) {
    const dateTimeValues = getDateTimeValuesFromSessions(
      formValues.sessions,
      formValues.timezoneCode
    );

    if (dateTimeValues) {
      return dateTimeValues;
    }
  }

  return {
    datetimeScheduled: formValues.datetimeScheduled,
    datetimeEnd: formValues.datetimeEnd
  };
};

const customMappings: Record<string, string> = {
  Going: 'confirmed',
  'Not going': 'cancelled'
};

export const createIdsToStatus = (enumObj: Record<string, number>): Record<number, string> => {
  return Object.entries(enumObj).reduce((acc, [key, value]) => {
    if (customMappings[key]) {
      acc[value] = customMappings[key];
    } else {
      acc[value] = key.toLowerCase();
    }
    return acc;
  }, {} as Record<number, string>);
};

export const idsToStatusReminders = createIdsToStatus(REMINDERS_GUEST_LIST);

// Update the format functions to use idsToStatusReminders
export const formatSlackRemindersForDB = (slackReminders: Reminder[]): any => {
  const formattedSlackReminders = slackReminders.map(
    ({ uuid, notifyStatus, isCreated, selectedGuestStatuses, ...rest }) => {
      const mappedStatuses = selectedGuestStatuses
        ? selectedGuestStatuses.map((statusId) => idsToStatusReminders[statusId])
        : [];
      if (rest.title === '' && notifyStatus === REMINDER_STATUS.SENT) {
        return { ...rest, title: 'No title' };
      }

      return { ...rest, selectedGuestStatuses: mappedStatuses };
    }
  );
  return formattedSlackReminders;
};

export const formatEmailRemindersForDB = (emailReminders: EmailReminder[]): any => {
  const formattedEmailReminders = emailReminders.map(
    ({ uuid, notifyStatus, isCreated, selectedGuestStatuses, ...rest }) => {
      const mappedStatuses = selectedGuestStatuses
        ? selectedGuestStatuses.map((statusId) => idsToStatusReminders[statusId])
        : [];
      return { ...rest, selectedGuestStatuses: mappedStatuses };
    }
  );

  return formattedEmailReminders;
};
